import React, { useEffect } from "react";
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Outlet,
  RouteObject,
  useRouteError,
  Link,
} from "react-router-dom";

import NonAuthLayout from "../Layouts/NonAuthLayout";
import VerticalLayout from "../Layouts/index";
import SuperAdminLayout from "../Layouts/SuperAdminLayout";
import {
  authProtectedRoutes,
  publicRoutes,
  superAdminProtectedRoutes,
} from "./allRoutes";
import AuthProtected from "./AuthProtected";
import SuperAdminProtected from "./SuperAdminProtected";
import BillingSoftLockGuard from "../Components/Billing/BillingSoftLockGuard";
import { UnsavedChangesProvider } from "../contexts/UnsavedChangesContext";
import { isIntlPortalHost } from "../helpers/brandPortal";

/**
 * Fallback amigável do data router (RR7).
 * Por quê: teardown de ApexCharts/libs DOM no unmount SPA caía no “Unexpected
 * Application Error!” cru — F5 “curava”. Mensagem leiga + reload; causa raiz =
 * libs com destroy seguro + shell estável (DOC 09 § 7.4).
 */
function AppRouteError() {
  const error = useRouteError();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="page-content">
      <div className="container-fluid py-5 text-center">
        <h4 className="mb-2">
          {isNotFound
            ? "Página não encontrada"
            : "Algo deu errado ao abrir esta tela"}
        </h4>
        <p className="text-muted mb-4">
          {isNotFound
            ? "O endereço pode ter mudado. Volte ao painel e tente de novo."
            : "Atualize a página. Se continuar, volte ao painel e abra o menu de novo."}
        </p>
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Atualizar página
          </button>
          <Link to="/dashboard" className="btn btn-soft-secondary">
            Ir ao painel
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Em app.brsend.com, títulos i18n ainda dizem “Br Envio” — alinha o document.title à marca da porta.
 */
function BrandDocumentTitleSync() {
  useEffect(() => {
    if (!isIntlPortalHost()) return;

    const sync = () => {
      if (document.title.includes("Br Envio")) {
        document.title = document.title.replace(/Br Envio/g, "Br Send");
      }
    };
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.querySelector("title") || document.head, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => obs.disconnect();
  }, []);

  return null;
}

/**
 * Layout raiz do data router — useBlocker precisa viver abaixo do RouterProvider.
 * Por quê: form sujo na Conta confirma navegação SPA (LAC-078), não só beforeunload.
 */
function RootLayout() {
  return (
    <>
      <BrandDocumentTitleSync />
      <UnsavedChangesProvider>
        <Outlet />
      </UnsavedChangesProvider>
    </>
  );
}

function mapPublicRoutes(): RouteObject[] {
  return publicRoutes.map((route) => ({
    path: route.path,
    element: <NonAuthLayout>{route.component}</NonAuthLayout>,
  }));
}

/**
 * Shell SA estável — Outlet troca só a página; header/sidebar não remountam.
 */
function SuperAdminShell() {
  return (
    <SuperAdminProtected>
      <SuperAdminLayout>
        <Outlet />
      </SuperAdminLayout>
    </SuperAdminProtected>
  );
}

/**
 * Shell tenant estável — Auth + soft-lock + layout fora das rotas-folha.
 * Por quê: antes cada path remountava VerticalLayout/SimpleBar/Apex no unmount
 * e o RR7 mostrava erro na rota de destino (ex.: /settings/api-tokens).
 */
function TenantShell() {
  return (
    <AuthProtected>
      <BillingSoftLockGuard>
        <VerticalLayout>
          <Outlet />
        </VerticalLayout>
      </BillingSoftLockGuard>
    </AuthProtected>
  );
}

function mapSuperAdminRoutes(): RouteObject {
  return {
    element: <SuperAdminShell />,
    children: superAdminProtectedRoutes.map((route) => ({
      path: route.path,
      element: route.component,
    })),
  };
}

function mapTenantRoutes(): RouteObject {
  return {
    element: <TenantShell />,
    children: authProtectedRoutes.map((route) => ({
      path: route.path,
      element: route.component,
    })),
  };
}

/** Router do painel — createBrowserRouter habilita useBlocker (RR 7). */
export function createAppRouter() {
  return createBrowserRouter(
    [
      {
        element: <RootLayout />,
        errorElement: <AppRouteError />,
        children: [
          ...mapPublicRoutes(),
          mapSuperAdminRoutes(),
          mapTenantRoutes(),
        ],
      },
    ],
    { basename: process.env.PUBLIC_URL || undefined }
  );
}
